import {Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {
  base64Decode,
  CancelledError,
  DateService,
  DownloadStore,
  isDefined,
  QueryStore,
  saveAs,
  SOFTLINE_FEATURE_DOWNLOAD,
  SOFTLINE_SERVICE_UUID,
  Store,
  Transaction } from '@softline/core';
import {
  ApplicationModule,
  BackNavigable,
  BackNavigationService,
  File,
  showRequestErrors,
  Refreshable,
  RefreshService,
  SOFTLINE_API_FILE
} from '@softline/application';
import {map} from 'rxjs/operators';
import {Observable, of, Subscription} from 'rxjs';
import {RdpLogAppStore, SOFTLINE_FEATURE_RDP_LOG_APP} from '../../rdp-log.shared';
import {MessageBarStore, SOFTLINE_FEATURE_MESSAGE_BAR} from '@softline/ui-core';
import {RdpLogEntry} from '../../data/rdp-log-entry.model';
import {RdpLogQuery} from '../../data/rdp-log-query.model';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {CommonModule} from '@angular/common';

import { ChipComponent } from '@softline/ui-core';
import { ChipGroupComponent } from '@softline/ui-core';
import { LoadingSpinnerComponent } from '@softline/ui-core';
import { LogPipe } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
    selector: 'lib-rdp-overview',
    imports: [CommonModule, ApplicationModule, RouterModule,
    ChipComponent,
    ChipGroupComponent,
    LoadingSpinnerComponent,
    LogPipe,
    TranslatePipe],
    templateUrl: './rdp-overview.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./rdp-overview.component.css']
})
export class RdpOverviewComponent implements OnInit, OnDestroy, BackNavigable, Refreshable {

  private subscription?: Subscription;

  logs$: Observable<readonly RdpLogEntry[]> = this.store.observe(SOFTLINE_FEATURE_RDP_LOG_APP, RdpLogAppStore.getters.all);

  total$ = this.logs$.pipe(map(o => o.length));

  query$ = this.store.observe(SOFTLINE_FEATURE_RDP_LOG_APP, QueryStore.getters.query.query)
    .pipe(map(o => o as any));

  loading$ = this.store.observe(SOFTLINE_FEATURE_RDP_LOG_APP, RdpLogAppStore.getters.loading);
  loaded$ = this.store.observe(SOFTLINE_FEATURE_RDP_LOG_APP, RdpLogAppStore.getters.loaded);

  // downloadRequests$ = this.store.observe(SOFTLINE_FEATURE_DOWNLOAD, DownloadStore.getters.subscription);
  downloadRequests$: Observable<Transaction[]> = of([]);

  constructor(private store: Store,
              @Inject(SOFTLINE_SERVICE_UUID) private uuid: () => string,
              private dateService: DateService,
              private route: ActivatedRoute,
              protected router: Router,
              private backNavigationService: BackNavigationService,
              private refreshService: RefreshService) { }

  ngOnInit(): void {
    this.backNavigationService.set(this);
    this.refreshService.add(this);
    this.subscription = this.route.queryParamMap
      .pipe(map(o => o.get('query')))
      .subscribe( encodedQuery => this.load(encodedQuery));
  }

  ngOnDestroy(): void {
    if (this.subscription && !this.subscription.closed)
      this.subscription.unsubscribe();
    this.subscription = undefined;
    this.backNavigationService.set(undefined);
    this.refreshService.remove(this);
  }

  async refresh(): Promise<void> {
    const encodedQuery = this.route.snapshot.queryParamMap.get('query');
    await this.load(encodedQuery);
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/']);
  }

  async load(encodedQuery: string | null | undefined): Promise<void> {
    let query: RdpLogQuery;
    if (!isDefined(encodedQuery))
      query = {from: this.dateService.now(), to: this.dateService.now()};
    else
      query = JSON.parse(base64Decode(encodedQuery));

    try {
      this.store.commit(SOFTLINE_FEATURE_RDP_LOG_APP, RdpLogAppStore.mutations.query.setQuery, query);
      await this.store.dispatch(SOFTLINE_FEATURE_RDP_LOG_APP, RdpLogAppStore.actions.loadMany, { queryParams: { query }, clear: true});
    }
    catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  async downloadFile(file: File): Promise<void> {
    let loadingBarId: string | undefined;
    try {
      const token = this.uuid();
      loadingBarId = await this.store.dispatch(SOFTLINE_FEATURE_MESSAGE_BAR, MessageBarStore.actions.loading, {
        message: file.name,
        onDismiss: () => this.store.dispatch(SOFTLINE_FEATURE_DOWNLOAD, DownloadStore.actions.cancel, token)
      });
      const result = await this.store.dispatch(SOFTLINE_FEATURE_DOWNLOAD, DownloadStore.actions.download,
        {location: {path: SOFTLINE_API_FILE, pathParams: {key: file}}, filename: file.name, token }
      );
      saveAs(result.content, result.filename);
      await this.store.dispatch(SOFTLINE_FEATURE_MESSAGE_BAR, MessageBarStore.actions.success, '#RDP_LOG.MESSAGE.DOWNLOAD_SUCCESS');
    } catch (e) {
      if (e instanceof CancelledError) {
        await this.store.dispatch(SOFTLINE_FEATURE_MESSAGE_BAR, MessageBarStore.actions.info, 'Der Download wurde abgebrochen');
      }
      else {
        await this.store.dispatch(SOFTLINE_FEATURE_MESSAGE_BAR, MessageBarStore.actions.close, loadingBarId);
        const msgParams: any = {
          message: '#RDP_LOG.MESSAGE.DOWNLOAD_ERROR',
          actions: [
            {
              title: '#RDP_LOG.MESSAGE.ERNEUT_VERSUCHEN',
              type: 'link',
              onExecute: async () => this.downloadFile(file)
            }
          ]
        };
        await this.store.dispatch(SOFTLINE_FEATURE_MESSAGE_BAR, MessageBarStore.actions.error, msgParams);
      }
    }
  }
}
